import {
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Icon,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Text,
  Badge,
  HStack,
  Spinner,
  Switch,
  Divider,
  Radio,
  RadioGroup,
  Stack,
} from '@chakra-ui/react'
import { useState, useEffect } from 'react'
import {
  LuDownload,
  LuFileJson,
  LuFileSpreadsheet,
  LuFileText,
  LuChevronDown,
} from 'react-icons/lu'
import { useMyProfile } from '../../../api/api_hooks/user/myProfile'
import { IAnalysisMalaria } from '../../../types/ia-analysis'
import jsPDF from 'jspdf'

interface AnalysisResult {
  infectedRbcCount: number
  uninfectedRbcCount: number
  totalCells: number
  parasitemiaPercent: number
}

interface AnalysisTiming {
  perImage: number[]
  totalMs: number
  analyzedCount: number
  totalCount: number
}

interface ParasitologyExportButtonProps {
  slideId: string
  analysisResults: AnalysisResult | null
  individualResults: IAnalysisMalaria[]
  imageUrls: string[]
  analysisTiming: AnalysisTiming | null
  isDisabled?: boolean
}

// Load an image URL into an HTMLImageElement
const loadImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new window.Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = url
  })
}

// Draw bounding boxes on a canvas and return base64
const renderImageWithBoxes = async (
  imageUrl: string,
  positiveBoxes: number[][] | undefined,
  negativeBoxes: number[][] | undefined,
  maxWidth: number = 500,
  boxFilter: 'all' | 'positiveOnly' | 'negativeOnly' = 'all',
): Promise<string | null> => {
  try {
    const img = await loadImage(imageUrl)
    const scale = Math.min(maxWidth / img.naturalWidth, 1)
    const w = Math.round(img.naturalWidth * scale)
    const h = Math.round(img.naturalHeight * scale)

    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) return null

    ctx.drawImage(img, 0, 0, w, h)

    const drawBoxes = (boxes: number[][] | undefined, color: string) => {
      if (!boxes || boxes.length === 0) return
      ctx.strokeStyle = color
      ctx.lineWidth = 2

      // Detect if normalized (0-1) or absolute
      const sample = boxes[0]
      const maxCoord = Math.max(Math.abs(sample[0]), Math.abs(sample[1]), Math.abs(sample[2]), Math.abs(sample[3]))
      const isNormalized = maxCoord <= 1

      boxes.forEach(([x1, y1, x2, y2]) => {
        let px1: number, py1: number, px2: number, py2: number
        if (isNormalized) {
          px1 = x1 * w; py1 = y1 * h; px2 = x2 * w; py2 = y2 * h
        } else {
          px1 = x1 * scale; py1 = y1 * scale; px2 = x2 * scale; py2 = y2 * scale
        }
        ctx.strokeRect(px1, py1, px2 - px1, py2 - py1)
      })
    }

    if (boxFilter !== 'positiveOnly') {
      drawBoxes(negativeBoxes, '#38B2AC')   // green/teal for negatives
    }
    if (boxFilter !== 'negativeOnly') {
      drawBoxes(positiveBoxes, '#FF6B35')   // orange for positives
    }

    return canvas.toDataURL('image/jpeg', 0.85)
  } catch (_e) {
    return null
  }
}

export default function ParasitologyExportButton({
  slideId,
  analysisResults,
  individualResults,
  imageUrls,
  analysisTiming,
  isDisabled = false,
}: ParasitologyExportButtonProps) {
  const toast = useToast()
  const { isOpen: isPdfModalOpen, onOpen: onPdfModalOpen, onClose: onPdfModalClose } = useDisclosure()
  const { data: userProfile } = useMyProfile()

  const [exporting, setExporting] = useState<string | null>(null)
  const [pdfOptions, setPdfOptions] = useState({
    organizationName: '',
    includeImages: true,
    positiveOnly: false,
    boxFilter: 'all' as 'all' | 'positiveOnly' | 'negativeOnly',
  })

  useEffect(() => {
    if (userProfile?.companyName) {
      setPdfOptions(prev => ({ ...prev, organizationName: userProfile.companyName }))
    }
  }, [userProfile?.companyName])

  // Filter results based on positiveOnly toggle
  const getFilteredData = () => {
    let results = individualResults
    let urls = imageUrls
    let timing = analysisTiming

    if (pdfOptions.positiveOnly) {
      const indices: number[] = []
      results = individualResults.filter((r, i) => {
        if (r.infectedRbcCount > 0) {
          indices.push(i)
          return true
        }
        return false
      })
      urls = indices.map(i => imageUrls[i])
      if (analysisTiming) {
        timing = {
          ...analysisTiming,
          perImage: indices.map(i => analysisTiming.perImage[i]).filter(Boolean),
          analyzedCount: results.length,
        }
      }
    }

    // Recompute aggregated
    const agg = results.reduce(
      (acc, r) => {
        acc.infectedRbcCount += r.infectedRbcCount
        acc.uninfectedRbcCount += r.uninfectedRbcCount
        return acc
      },
      { infectedRbcCount: 0, uninfectedRbcCount: 0, totalCells: 0, parasitemiaPercent: 0 }
    )
    agg.totalCells = agg.infectedRbcCount + agg.uninfectedRbcCount
    agg.parasitemiaPercent = agg.totalCells > 0 ? (agg.infectedRbcCount / agg.totalCells) * 100 : 0

    return { results, urls, timing, aggregated: agg }
  }

  // ─── PDF Export ───
  const handleExportPDF = async () => {
    setExporting('pdf')
    onPdfModalClose()

    try {
      const { results, urls, timing, aggregated } = getFilteredData()
      const doc = new jsPDF()
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const margin = 20
      let y = 20

      const checkNewPage = (space: number = 30) => {
        if (y > pageHeight - space) {
          doc.addPage()
          y = 20
        }
      }

      // ── Header ──
      doc.setFontSize(18)
      doc.setFont('helvetica', 'bold')
      doc.text('Rapport d\'Analyse Parasitologique', pageWidth / 2, y, { align: 'center' })
      y += 8
      doc.setFontSize(12)
      doc.setFont('helvetica', 'normal')
      doc.text('Analyse de frottis sanguin - Détection de patterns', pageWidth / 2, y, { align: 'center' })
      y += 6
      doc.setFontSize(10)
      doc.setTextColor(100)
      doc.text(new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }), pageWidth / 2, y, { align: 'center' })
      doc.setTextColor(0)
      y += 12

      // ── Identification ──
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.text('Identification', margin, y)
      y += 7
      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      doc.text(`Slide ID: ${slideId}`, margin, y); y += 5
      if (pdfOptions.organizationName) {
        doc.text(`Organisation: ${pdfOptions.organizationName}`, margin, y); y += 5
      }
      doc.text(`Date d'analyse: ${new Date().toLocaleDateString('fr-FR')}`, margin, y); y += 5
      if (pdfOptions.positiveOnly) {
        doc.setTextColor(200, 100, 0)
        doc.text('Mode: Positifs uniquement', margin, y)
        doc.setTextColor(0)
        y += 5
      }
      y += 7

      // ── Résumé global ──
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.text('Résumé des résultats', margin, y)
      y += 7

      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      doc.text(`Images analysées: ${results.length}`, margin, y); y += 5
      doc.text(`Cellules infectées (pattern+): ${aggregated.infectedRbcCount}`, margin, y); y += 5
      doc.text(`Cellules saines (pattern-): ${aggregated.uninfectedRbcCount}`, margin, y); y += 5
      doc.text(`Total cellules: ${aggregated.totalCells}`, margin, y); y += 5
      doc.text(`Densité parasitaire: ${aggregated.parasitemiaPercent.toFixed(2)}%`, margin, y); y += 5

      if (timing) {
        doc.text(`Temps total d'analyse: ${(timing.totalMs / 1000).toFixed(1)}s`, margin, y); y += 5
        if (timing.perImage.length > 1) {
          const avg = timing.perImage.reduce((a, b) => a + b, 0) / timing.perImage.length
          doc.text(`Temps moyen par image: ${(avg / 1000).toFixed(1)}s`, margin, y); y += 5
        }
      }
      y += 5

      // ── Résultat global ──
      checkNewPage(20)
      doc.setFontSize(11)
      doc.setFont('helvetica', 'bold')
      if (aggregated.parasitemiaPercent > 0) {
        doc.setTextColor(200, 100, 0)
        doc.text(`RÉSULTAT: PATTERN POSITIF (${aggregated.parasitemiaPercent.toFixed(2)}%)`, margin, y)
      } else {
        doc.setTextColor(0, 128, 0)
        doc.text('RÉSULTAT: PATTERN NÉGATIF', margin, y)
      }
      doc.setTextColor(0)
      y += 12

      // ── Détail par image ──
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.text('Détail par image', margin, y)
      y += 10

      for (let i = 0; i < results.length; i++) {
        const result = results[i]
        const imageUrl = urls[i]
        const totalCells = (result.infectedRbcCount ?? 0) + (result.uninfectedRbcCount ?? 0)
        const parasitemia = totalCells > 0 ? ((result.infectedRbcCount ?? 0) / totalCells) * 100 : 0
        const imgTime = timing?.perImage[i]

        if (pdfOptions.includeImages && imageUrl) {
          // Image + info needs ~110px
          checkNewPage(120)

          // Render image with bounding boxes
          const annotatedBase64 = await renderImageWithBoxes(
            imageUrl,
            result.positiveBoxes,
            result.negativeBoxes,
            400,
            pdfOptions.boxFilter
          )

          if (annotatedBase64) {
            const maxImgW = pageWidth - margin * 2
            const maxImgH = 80

            // Load to get dimensions
            const tmpImg = await loadImage(annotatedBase64)
            const aspect = tmpImg.naturalWidth / tmpImg.naturalHeight
            let imgW = maxImgW
            let imgH = imgW / aspect
            if (imgH > maxImgH) {
              imgH = maxImgH
              imgW = imgH * aspect
            }

            try {
              doc.addImage(annotatedBase64, 'JPEG', margin, y, imgW, imgH)
              y += imgH + 3
            } catch (_e) {
              // skip image if it fails
            }
          }
        } else {
          checkNewPage(30)
        }

        // Image info
        doc.setFontSize(10)
        doc.setFont('helvetica', 'bold')
        const label = `Image #${i + 1}`
        const timeStr = imgTime ? ` (${(imgTime / 1000).toFixed(1)}s)` : ''
        doc.text(`${label}${timeStr}`, margin, y); y += 5

        doc.setFont('helvetica', 'normal')
        doc.setFontSize(9)
        doc.text(`Pattern+: ${result.infectedRbcCount ?? 0}  |  Pattern-: ${result.uninfectedRbcCount ?? 0}  |  Total: ${totalCells}  |  Densité: ${parasitemia.toFixed(2)}%`, margin, y)
        y += 8

        // Separator line
        doc.setDrawColor(220)
        doc.line(margin, y, pageWidth - margin, y)
        doc.setDrawColor(0)
        y += 5
      }

      // ── Disclaimer ──
      checkNewPage(40)
      y += 5
      doc.setFontSize(10)
      doc.setFont('helvetica', 'bold')
      doc.text('Avertissement', margin, y); y += 6
      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(100)
      const disclaimer = 'Ce rapport est généré à titre éducatif et de recherche uniquement. Les résultats de détection automatique ne constituent pas un diagnostic médical. Toute interprétation doit être validée par un professionnel de santé qualifié. Les performances du modèle dépendent de la qualité des images et des conditions d\'acquisition.'
      const disclaimerLines = doc.splitTextToSize(disclaimer, pageWidth - margin * 2)
      doc.text(disclaimerLines, margin, y)
      y += disclaimerLines.length * 4

      // ── Footer (page numbers) ──
      const totalPages = doc.getNumberOfPages()
      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p)
        doc.setFontSize(8)
        doc.setTextColor(150)
        doc.text(`Careflai - Rapport Parasitologie - ${slideId}`, margin, pageHeight - 10)
        doc.text(`Page ${p}/${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' })
      }

      doc.save(`parasitology-report-${slideId}-${new Date().toISOString().slice(0, 10)}.pdf`)

      toast({
        title: 'PDF exporté',
        description: `Rapport généré pour ${results.length} image(s)`,
        status: 'success',
        duration: 3000,
      })
    } catch (error: any) {
      console.error('PDF export error:', error)
      toast({
        title: 'Erreur export PDF',
        description: error.message || 'Échec de la génération du PDF',
        status: 'error',
        duration: 4000,
      })
    } finally {
      setExporting(null)
    }
  }

  // ─── JSON Export ───
  const handleExportJSON = () => {
    setExporting('json')
    try {
      const { results, timing, aggregated } = getFilteredData()
      const data = {
        slideId,
        exportDate: new Date().toISOString(),
        organization: pdfOptions.organizationName,
        summary: aggregated,
        timing: timing ? {
          totalMs: timing.totalMs,
          perImage: timing.perImage,
          avgMs: timing.perImage.length > 0 ? timing.perImage.reduce((a, b) => a + b, 0) / timing.perImage.length : 0,
        } : null,
        images: results.map((r, i) => ({
          index: i + 1,
          id: r.id,
          infectedRbcCount: r.infectedRbcCount,
          uninfectedRbcCount: r.uninfectedRbcCount,
          parasitemiaPercent: r.estimatedParasitemiaPercent ?? ((r.infectedRbcCount + r.uninfectedRbcCount) > 0 ? (r.infectedRbcCount / (r.infectedRbcCount + r.uninfectedRbcCount)) * 100 : 0),
          positiveBoxes: r.positiveBoxes,
          negativeBoxes: r.negativeBoxes,
          analysisTimeMs: timing?.perImage[i] ?? null,
        })),
      }
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `parasitology-${slideId}-${new Date().toISOString().slice(0, 10)}.json`
      a.click()
      URL.revokeObjectURL(url)
      toast({ title: 'JSON exporté', status: 'success', duration: 2000 })
    } catch (error: any) {
      toast({ title: 'Erreur export JSON', description: error.message, status: 'error', duration: 3000 })
    } finally {
      setExporting(null)
    }
  }

  // ─── CSV Export ───
  const handleExportCSV = () => {
    setExporting('csv')
    try {
      const { results, timing, aggregated } = getFilteredData()
      const header = 'Image #,Infected,Uninfected,Total,Parasitemia %,Analysis Time (s)'
      const rows = results.map((r, i) => {
        const total = (r.infectedRbcCount ?? 0) + (r.uninfectedRbcCount ?? 0)
        const parasitemia = total > 0 ? ((r.infectedRbcCount ?? 0) / total) * 100 : 0
        const time = timing?.perImage[i] ? (timing.perImage[i] / 1000).toFixed(1) : '-'
        return `${i + 1},${r.infectedRbcCount ?? 0},${r.uninfectedRbcCount ?? 0},${total},${parasitemia.toFixed(2)},${time}`
      })
      // Total row
      const totalTime = timing ? (timing.totalMs / 1000).toFixed(1) : '-'
      rows.push(`TOTAL,${aggregated.infectedRbcCount},${aggregated.uninfectedRbcCount},${aggregated.totalCells},${aggregated.parasitemiaPercent.toFixed(2)},${totalTime}`)

      const csv = [header, ...rows].join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `parasitology-${slideId}-${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      URL.revokeObjectURL(url)
      toast({ title: 'CSV exporté', status: 'success', duration: 2000 })
    } catch (error: any) {
      toast({ title: 'Erreur export CSV', description: error.message, status: 'error', duration: 3000 })
    } finally {
      setExporting(null)
    }
  }

  return (
    <>
      <Menu>
        <MenuButton
          as={Button}
          colorScheme="teal"
          variant="outline"
          size="sm"
          leftIcon={exporting ? <Spinner size="xs" /> : <Icon as={LuDownload} />}
          rightIcon={<Icon as={LuChevronDown} />}
          isDisabled={isDisabled || !!exporting}
        >
          {exporting ? 'Export...' : 'Export'}
        </MenuButton>
        <MenuList>
          <MenuItem
            icon={<Icon as={LuFileText} />}
            onClick={onPdfModalOpen}
            isDisabled={!!exporting}
          >
            <HStack justify="space-between" w="100%">
              <Text>Rapport PDF</Text>
              <Badge colorScheme="green" fontSize="xs">Imprimable</Badge>
            </HStack>
          </MenuItem>
          <MenuDivider />
          <MenuItem
            icon={<Icon as={LuFileJson} />}
            onClick={handleExportJSON}
            isDisabled={!!exporting}
          >
            <HStack justify="space-between" w="100%">
              <Text>JSON Data</Text>
              <Badge colorScheme="blue" fontSize="xs">Dev</Badge>
            </HStack>
          </MenuItem>
          <MenuItem
            icon={<Icon as={LuFileSpreadsheet} />}
            onClick={handleExportCSV}
            isDisabled={!!exporting}
          >
            <HStack justify="space-between" w="100%">
              <Text>CSV / Excel</Text>
              <Badge colorScheme="purple" fontSize="xs">Stats</Badge>
            </HStack>
          </MenuItem>
        </MenuList>
      </Menu>

      {/* PDF Options Modal */}
      <Modal isOpen={isPdfModalOpen} onClose={onPdfModalClose} size="md">
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={2}>
              <Icon as={LuFileText} color="teal.500" />
              <Text>Options du rapport PDF</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm">Organisation</FormLabel>
                <Input
                  value={pdfOptions.organizationName}
                  onChange={(e) => setPdfOptions(prev => ({ ...prev, organizationName: e.target.value }))}
                  placeholder="Nom de l'organisation"
                  size="sm"
                />
              </FormControl>

              <Divider />

              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <VStack align="start" spacing={0}>
                  <FormLabel htmlFor="include-images" mb={0} fontSize="sm">
                    Inclure les images annotées
                  </FormLabel>
                  <Text fontSize="xs" color="gray.500">
                    Images avec bounding boxes (positifs en orange, négatifs en vert)
                  </Text>
                </VStack>
                <Switch
                  id="include-images"
                  colorScheme="teal"
                  isChecked={pdfOptions.includeImages}
                  onChange={(e) => setPdfOptions(prev => ({ ...prev, includeImages: e.target.checked }))}
                />
              </FormControl>

              {pdfOptions.includeImages && (
                <FormControl>
                  <FormLabel fontSize="sm" mb={2}>Bounding boxes affichées</FormLabel>
                  <RadioGroup
                    value={pdfOptions.boxFilter}
                    onChange={(val) => setPdfOptions(prev => ({ ...prev, boxFilter: val as 'all' | 'positiveOnly' | 'negativeOnly' }))}
                  >
                    <Stack direction="row" spacing={4}>
                      <Radio value="all" colorScheme="purple" size="sm">Toutes</Radio>
                      <Radio value="positiveOnly" colorScheme="orange" size="sm">Positives</Radio>
                      <Radio value="negativeOnly" colorScheme="teal" size="sm">Négatives</Radio>
                    </Stack>
                  </RadioGroup>
                </FormControl>
              )}

              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <VStack align="start" spacing={0}>
                  <FormLabel htmlFor="positive-only" mb={0} fontSize="sm">
                    Positifs uniquement
                  </FormLabel>
                  <Text fontSize="xs" color="gray.500">
                    N'inclure que les images avec au moins 1 cellule positive
                  </Text>
                </VStack>
                <Switch
                  id="positive-only"
                  colorScheme="orange"
                  isChecked={pdfOptions.positiveOnly}
                  onChange={(e) => setPdfOptions(prev => ({ ...prev, positiveOnly: e.target.checked }))}
                />
              </FormControl>

              {/* Preview info */}
              <HStack bg="gray.50" p={3} borderRadius="md" spacing={4} fontSize="xs" color="gray.600">
                <Text>
                  {pdfOptions.positiveOnly
                    ? `${individualResults.filter(r => r.infectedRbcCount > 0).length} image(s) positive(s)`
                    : `${individualResults.length} image(s) au total`
                  }
                </Text>
                {analysisTiming && (
                  <Text>Temps total: {(analysisTiming.totalMs / 1000).toFixed(1)}s</Text>
                )}
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onPdfModalClose}>
              Annuler
            </Button>
            <Button
              colorScheme="teal"
              onClick={handleExportPDF}
              isLoading={exporting === 'pdf'}
              loadingText="Génération..."
              leftIcon={<Icon as={LuFileText} />}
            >
              Générer le PDF
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
